// Guide & Tips — static reference cards. Ported verbatim from the legacy
// views.guide; the only change is the `(app)` mount-element signature.
export function guideView(app) {
  app.innerHTML = `
    <section class="space-y-6">
      <div>
        <h2 class="text-2xl font-bold text-sage-800 mb-1">Guide & Tips</h2>
        <p class="text-sage-600 text-sm">Memory tricks and patterns to help you internalize Spanish verbs faster.</p>
      </div>

      <article class="bg-white rounded-2xl border border-sage-100 p-6">
        <h3 class="font-bold text-lg mb-2">📐 Regular verbs — the foundation</h3>
        <p class="text-sm text-sage-700 mb-3">Spanish has three regular conjugation patterns based on the infinitive ending:</p>
        <div class="grid sm:grid-cols-3 gap-3 text-sm">
          <div class="bg-sage-50 rounded-lg p-3">
            <p class="font-bold text-sage-800">-AR verbs</p>
            <p class="text-xs text-sage-500 mb-2">hablar (to speak)</p>
            <p class="font-mono text-xs leading-relaxed">o, as, a,<br>amos, áis, an</p>
          </div>
          <div class="bg-sage-50 rounded-lg p-3">
            <p class="font-bold text-sage-800">-ER verbs</p>
            <p class="text-xs text-sage-500 mb-2">comer (to eat)</p>
            <p class="font-mono text-xs leading-relaxed">o, es, e,<br>emos, éis, en</p>
          </div>
          <div class="bg-sage-50 rounded-lg p-3">
            <p class="font-bold text-sage-800">-IR verbs</p>
            <p class="text-xs text-sage-500 mb-2">vivir (to live)</p>
            <p class="font-mono text-xs leading-relaxed">o, es, e,<br>imos, ís, en</p>
          </div>
        </div>
        <p class="text-xs text-sage-500 mt-3">💡 Tip: -ER and -IR verbs are identical except for nosotros and vosotros.</p>
      </article>

      <article class="bg-white rounded-2xl border border-sage-100 p-6">
        <h3 class="font-bold text-lg mb-2">🔄 Stem-changing verbs — the boot</h3>
        <p class="text-sm text-sage-700 mb-3">The "boot" or "shoe" pattern: stem changes happen in all forms EXCEPT nosotros and vosotros (the heel of the boot). If you draw a line around the four changed forms, it looks like a boot.</p>
        <div class="bg-sage-50 rounded-lg p-3 text-sm font-mono mb-3 max-w-xs">
          <div class="grid grid-cols-2 gap-1">
            <div class="bg-red-100 text-red-700 px-2 py-1 rounded">pienso</div>
            <div class="bg-red-100 text-red-700 px-2 py-1 rounded">piensas</div>
            <div class="bg-red-100 text-red-700 px-2 py-1 rounded">piensa</div>
            <div class="px-2 py-1">pensamos</div>
            <div class="px-2 py-1">pensáis</div>
            <div class="bg-red-100 text-red-700 px-2 py-1 rounded">piensan</div>
          </div>
        </div>
        <ul class="text-sm text-sage-700 space-y-1 list-disc pl-5">
          <li><b>e → ie</b>: pensar, querer, entender, empezar</li>
          <li><b>o → ue</b>: poder, dormir, volver, contar</li>
          <li><b>e → i</b> (only -ir): pedir, servir, repetir</li>
          <li><b>u → ue</b>: jugar (only one!)</li>
        </ul>
      </article>

      <article class="bg-white rounded-2xl border border-sage-100 p-6">
        <h3 class="font-bold text-lg mb-2">👤 Yo-only irregulars — the "go-go" verbs</h3>
        <p class="text-sm text-sage-700 mb-3">A group of verbs is completely regular except in the "yo" form, where they add or change to <b>-go</b>:</p>
        <div class="bg-sage-50 rounded-lg p-3 text-sm font-mono mb-3">
          tener → ten<span class="text-red-600 font-bold">go</span> · poner → pon<span class="text-red-600 font-bold">go</span> · salir → sal<span class="text-red-600 font-bold">go</span> · hacer → ha<span class="text-red-600 font-bold">go</span> · venir → ven<span class="text-red-600 font-bold">go</span> · decir → di<span class="text-red-600 font-bold">go</span> · traer → trai<span class="text-red-600 font-bold">go</span>
        </div>
        <p class="text-xs text-sage-500">💡 Memorize the list as a chant: "tengo, pongo, salgo, hago, vengo, digo, traigo."</p>
      </article>

      <article class="bg-white rounded-2xl border border-sage-100 p-6">
        <h3 class="font-bold text-lg mb-2">⚡ Fully irregular — the big four</h3>
        <p class="text-sm text-sage-700 mb-3">Four verbs are so common and so irregular they deserve dedicated memorization: <b>ser</b>, <b>estar</b>, <b>ir</b>, and <b>tener</b>. Strange fact: in the preterite tense, <i>ser</i> and <i>ir</i> are identical (fui, fuiste, fue…) — context decides which it is.</p>
        <ul class="text-sm text-sage-700 space-y-1 list-disc pl-5">
          <li><b>ser vs estar</b>: ser is for permanent traits (I am tall), estar for temporary states & location (I am tired, I am at home).</li>
          <li><b>saber vs conocer</b>: saber for facts/skills, conocer for people and places.</li>
          <li><b>ir + a + infinitive</b> = "going to" do something. Voy a comer = I'm going to eat.</li>
        </ul>
      </article>

      <article class="bg-white rounded-2xl border border-sage-100 p-6">
        <h3 class="font-bold text-lg mb-2">📅 The three core tenses</h3>
        <div class="space-y-3 text-sm text-sage-700">
          <div>
            <p class="font-bold text-sage-800">Presente — happening now or habitually</p>
            <p>"Yo hablo español" = I speak Spanish / I am speaking Spanish.</p>
          </div>
          <div>
            <p class="font-bold text-sage-800">Pretérito Indefinido — completed actions in the past</p>
            <p>"Hablé con María ayer" = I spoke with María yesterday. Use this for one-time, finished events.</p>
          </div>
          <div>
            <p class="font-bold text-sage-800">Futuro Simple — what will happen</p>
            <p>"Hablaré con María mañana" = I will speak with María tomorrow. <b>Quick win:</b> for all regular verbs, just add é, ás, á, emos, éis, án to the full infinitive — no chopping off endings.</p>
          </div>
        </div>
      </article>

      <article class="bg-white rounded-2xl border border-sage-100 p-6">
        <h3 class="font-bold text-lg mb-2">🪞 Reflexive verbs</h3>
        <p class="text-sm text-sage-700 mb-2">Verbs ending in <b>-se</b> are reflexive — the action is done to oneself. Conjugate normally, then add the matching pronoun (me, te, se, nos, os, se) before the verb.</p>
        <div class="bg-sage-50 rounded-lg p-3 font-mono text-sm">
          levantarse → me levanto, te levantas, se levanta…
        </div>
      </article>

      <article class="bg-white rounded-2xl border border-sage-100 p-6">
        <h3 class="font-bold text-lg mb-2">🎯 Study strategy</h3>
        <ol class="text-sm text-sage-700 space-y-2 list-decimal pl-5">
          <li><b>Start with the top 50.</b> They cover ~70% of everyday speech.</li>
          <li><b>Learn one tense at a time.</b> Master Presente before moving on.</li>
          <li><b>Drill the "go-go" verbs as a set.</b> They share a single shortcut.</li>
          <li><b>Use the trainer daily.</b> Even 5 minutes builds strong recall.</li>
          <li><b>Track mistakes.</b> Verbs you miss often deserve flashcard time.</li>
        </ol>
      </article>
    </section>
  `;
  window.scrollTo({ top: 0, behavior: "smooth" });
}
